import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  styled,
  Box,
  Icon,
} from "@mui/material";
import { useMemo } from "react";
import { getObjectDepth } from "src/utils/Utils";
import React from "react";
import { ExplainerTooltip } from "./ExplainerTooltip.js";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: theme.shadows.cardLg(theme.palette, theme.palette.mode),
  border: "none",
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  maxWidth: "100%",
}));

const StyledTableRowHead = styled(TableRow)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.overlay.white08
      : theme.palette.tableHeaderBg,
  "& td, & th": {
    border: 0,
    padding: theme.spacing(2, 2),
    borderBottom: "none",
    fontWeight: 700,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    border: 0,
    padding: theme.spacing(1.5, 1.5),
    borderBottom: `1px solid ${
      theme.palette.mode === "dark"
        ? theme.palette.overlay.white05
        : theme.palette.overlay.black03
    }`,
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

const ClickableText = styled(Typography)(({ theme, hasExplainer }) => ({
  position: "relative",
  display: "inline-block",
  ...(hasExplainer && {
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: theme.palette.text.secondary,
    textDecorationThickness: "1px",
    textUnderlineOffset: "2px",
    cursor: "help",
  }),
}));

const FeatureContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
}));

export const PlanComparisonTable = ({ sx, data }) => {
  const hasSubtitle = useMemo(
    () => getObjectDepth(data.body) === 3,
    [data.body]
  );

  const columnsWithTargets = useMemo(
    () => data.columns.filter((e) => Boolean(e.target)),
    [data.columns]
  );

  const renderCellContent = (content, isLastColumn = false) => {
    return (
      <Typography
        variant="body2"
        fontWeight={isLastColumn ? 700 : 500}
        sx={{
          color: isLastColumn ? "primary.main" : "text.primary",
          fontSize: "0.8rem",
        }}
      >
        {content}
      </Typography>
    );
  };

  const getExplainerText = (title, subtitle = null) => {
    if (hasSubtitle && subtitle) {
      return (
        data.body[title]?.[subtitle]?.explainer ||
        data.explainers?.[title]?.[subtitle]
      );
    }
    return data.body[title]?.explainer || data.explainers?.[title];
  };

  const getIcon = (title, subtitle = null) => {
    if (hasSubtitle && subtitle) {
      return data.body[title]?.[subtitle]?.icon;
    }
    return data.body[title]?.icon;
  };

  return (
    <Box sx={{ ...sx }}>
      <StyledTableContainer>
        <Table>
          <TableHead>
            <StyledTableRowHead>
              {data.columns.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align ?? "left"}
                  scope={column.scope ?? "auto"}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: "1.1rem",
                      color: "text.primary",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </StyledTableRowHead>
          </TableHead>
          <TableBody>
            {Object.keys(data.body).map((title) => (
              <React.Fragment key={title}>
                {hasSubtitle ? (
                  <>
                    {Object.keys(data.body[title]).map((subtitle) => (
                      <StyledTableRow key={`${title}-${subtitle}`}>
                        <TableCell component="th" scope="row">
                          <ExplainerTooltip
                            explainer={getExplainerText(title, subtitle)}
                            title={subtitle}
                          >
                            <FeatureContainer>
                              {getIcon(title, subtitle) && (
                                <Icon
                                  baseClassName="material-symbols-outlined"
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: "text.secondary",
                                    fontWeight: 300,
                                    marginTop: "2px",
                                  }}
                                >
                                  {getIcon(title, subtitle)}
                                </Icon>
                              )}
                              <ClickableText
                                variant="body2"
                                hasExplainer={Boolean(
                                  getExplainerText(title, subtitle)
                                )}
                                sx={{
                                  color: "text.primary",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {subtitle}
                              </ClickableText>
                            </FeatureContainer>
                          </ExplainerTooltip>
                        </TableCell>
                        {columnsWithTargets.map((column, columnIndex) => (
                          <TableCell key={columnIndex} align="center">
                            {renderCellContent(
                              data.body[title][subtitle][column.target],
                              columnIndex === columnsWithTargets.length - 1
                            )}
                          </TableCell>
                        ))}
                      </StyledTableRow>
                    ))}
                  </>
                ) : (
                  <StyledTableRow>
                    <TableCell component="th" scope="row">
                      <ExplainerTooltip
                        explainer={getExplainerText(title)}
                        title={title}
                      >
                        <FeatureContainer>
                          {getIcon(title) && (
                            <Icon
                              baseClassName="material-symbols-outlined"
                              sx={{
                                fontSize: "1.1rem",
                                color: "text.secondary",
                                fontWeight: 300,
                                marginTop: "2px",
                              }}
                            >
                              {getIcon(title)}
                            </Icon>
                          )}
                          <ClickableText
                            variant="body2"
                            hasExplainer={Boolean(getExplainerText(title))}
                            sx={{
                              color: "text.primary",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                            }}
                          >
                            {title}
                          </ClickableText>
                        </FeatureContainer>
                      </ExplainerTooltip>
                    </TableCell>
                    {columnsWithTargets.map((column, index) => (
                      <TableCell key={index} align="center">
                        {renderCellContent(
                          data.body[title][column.target],
                          index === columnsWithTargets.length - 1
                        )}
                      </TableCell>
                    ))}
                  </StyledTableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};
